import { useEffect, useRef } from 'react';
import { ROUTE_LABELS, type Route } from './routes';
import { Converter } from './Converter';
import { Editor } from './Editor';

interface RouteViewProps {
  route: Route;
}

/**
 * Hosts the central work area's two modes (cue-forge-a26: Studio (`edit`) folds
 * in QC and timing/sync; Convert folds in batch; all Pro gating is inline).
 *
 * Both panels stay MOUNTED and the inactive one is toggled with the `hidden`
 * attribute — standard ARIA tabpanel behavior for the rail's tablist — so
 * per-mode local state survives Studio<->Convert hops (cue-forge-drt.14): the
 * Studio find filter, row selection and Timing-panel toggle, and Convert's
 * chosen target format. Switching used to unmount the route and silently reset
 * all of these (e.g. a TTML target snapping back to VTT). Lazy subtrees are
 * unaffected: StudioPanel/AdvancedEditDialog still load only on demand, and a
 * hidden subtree's live regions stay silent by design.
 */
export function RouteView({ route }: RouteViewProps) {
  // `hidden` is display:none, not pause — a hidden subtree keeps playing media.
  // Silence the Studio video when the user hops to Convert (its playhead and
  // load state still survive, unlike the old unmount).
  const editRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (route === 'edit') return;
    editRef.current?.querySelectorAll<HTMLMediaElement>('video, audio').forEach((m) => m.pause());
  }, [route]);

  return (
    <>
      <div
        ref={editRef}
        role="tabpanel"
        id="cf-panel-edit"
        aria-labelledby="cf-tab-edit"
        aria-label={ROUTE_LABELS.edit}
        hidden={route !== 'edit'}
      >
        <Editor />
      </div>
      <div
        role="tabpanel"
        id="cf-panel-convert"
        aria-labelledby="cf-tab-convert"
        aria-label={ROUTE_LABELS.convert}
        hidden={route !== 'convert'}
      >
        <Converter />
      </div>
    </>
  );
}
